import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeft, ChevronDown, LoaderCircle, Save } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { selectTriggerStyles } from "../ui/select";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import { cn } from "../../lib/utils/utils";
import SeoFields from "../seo/SeoFields";

const EMPTY_FORM = {
  name: "",
  image: null,
  experience: "",
  designation: "",
  description: "",
  is_hod: false,
  speciality_ids: [],
  meta_title: "",
  meta_description: "",
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

const isMeaningfulHtml = (html) =>
  html && html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;

const asString = (value) => (value === undefined || value === null ? "" : String(value));

const DoctorFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [specialities, setSpecialities] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const isEditMode = mode === "edit";
  const pageTitle = useMemo(() => (isEditMode ? "Edit Doctor" : "Create Doctor"), [isEditMode]);

  useEffect(() => {
    const fetchSpecialities = async () => {
      try {
        const response = await apiClient.get("/specialities");
        setSpecialities(response.data?.data?.specialities || []);
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load specialities");
      }
    };

    fetchSpecialities();
  }, [showErrorToast]);

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchDoctor = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/doctors/${doctorId}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Doctor not found");
          navigate("/doctors");
          return;
        }
        setForm({
          name: item.name || "",
          image: null,
          experience: asString(item.experience),
          designation: item.designation || "",
          description: item.description || "",
          is_hod: [true, 1, "1", "true"].includes(item.is_hod),
          speciality_ids: item.speciality_ids || [],
          meta_title: item.meta_title || "",
          meta_description: item.meta_description || "",
        });
        setExistingImageUrl(item.image_url || "");
        setRemoveImage(false);
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load doctor");
        navigate("/doctors");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchDoctor();
  }, [doctorId, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "", ...(field === "is_hod" ? { image: "" } : {}) }));
  };

  const toggleSpeciality = (specialityId) => {
    setForm((current) => {
      const exists = current.speciality_ids.includes(specialityId);
      return {
        ...current,
        speciality_ids: exists
          ? current.speciality_ids.filter((id) => id !== specialityId)
          : [...current.speciality_ids, specialityId],
      };
    });
    setFormErrors((current) => ({ ...current, speciality_ids: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const experienceValue = asString(form.experience).trim();

    if (!form.name.trim()) nextErrors.name = "Name is required.";
    const hasSavedPhoto = existingImageUrl && !existingImageUrl.startsWith("/images/") && !removeImage;
    if (form.is_hod && !form.image && !hasSavedPhoto) nextErrors.image = "A photo is required for the head specialist.";
    if (!experienceValue) nextErrors.experience = "Experience is required.";
    if (Number.isNaN(Number(experienceValue)) || Number(experienceValue) < 0) nextErrors.experience = "Experience must be 0 or more.";
    if (!form.designation.trim()) nextErrors.designation = "Designation is required.";
    if (!isMeaningfulHtml(form.description)) nextErrors.description = "Description is required.";
    if (form.speciality_ids.length === 0) nextErrors.speciality_ids = "At least one speciality is required.";
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("experience", String(Number(asString(form.experience).trim())));
      payload.append("designation", form.designation.trim());
      payload.append("description", form.description);
      payload.append("is_hod", String(Boolean(form.is_hod)));
      payload.append("speciality_ids", JSON.stringify(form.speciality_ids));
      payload.append("meta_title", form.meta_title.trim());
      payload.append("meta_description", form.meta_description.trim());
      if (form.image) payload.append("image", form.image);
      if (isEditMode && removeImage && !form.image) payload.append("remove_image", "true");

      if (isEditMode) {
        await apiClient.put(`/doctors/${doctorId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Doctor updated successfully");
      } else {
        await apiClient.post("/doctors", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Doctor created successfully");
      }

      navigate("/doctors");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save doctor");
    } finally {
      setIsSaving(false);
    }
  };

  const selectedSpecialities = specialities.filter((item) => form.speciality_ids.includes(item.id));

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading doctor editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/doctors")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Doctors
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Name</label>
              <Input value={form.name} onChange={(event) => handleInputChange("name", event.target.value)} className={`rounded-lg ${formErrors.name ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.name}</FieldError>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Experience</label>
                <Input type="number" min="0" max="80" value={form.experience} onChange={(event) => handleInputChange("experience", event.target.value)} className={`rounded-lg ${formErrors.experience ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
                <FieldError>{formErrors.experience}</FieldError>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Designation</label>
                <Input value={form.designation} onChange={(event) => handleInputChange("designation", event.target.value)} className={`rounded-lg ${formErrors.designation ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
                <FieldError>{formErrors.designation}</FieldError>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Head Of Department</label>
              <label className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                <input type="checkbox" checked={form.is_hod} onChange={(event) => handleInputChange("is_hod", event.target.checked)} />
                Show as head specialist with a photo card
              </label>
              <p className="text-xs leading-relaxed text-slate-500">
                Heads appear with a photo card in Meet Our Specialists for all selected specialities. Other doctors appear in a list without photos.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Specialities</label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className={cn(selectTriggerStyles, formErrors.speciality_ids && "border-red-300 text-red-600")}>
                    <span className="min-w-0 flex-1 truncate">
                      {selectedSpecialities.length > 0
                        ? `${selectedSpecialities.length} selected`
                        : "Select specialities"}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                  <DropdownMenuLabel>Select specialities</DropdownMenuLabel>
                  {specialities.map((item) => (
                    <DropdownMenuCheckboxItem
                      key={item.id}
                      checked={form.speciality_ids.includes(item.id)}
                      onCheckedChange={() => toggleSpeciality(item.id)}
                      onSelect={(event) => event.preventDefault()}
                    >
                      <div className="flex min-w-0 flex-col overflow-hidden">
                        <span className="truncate" title={item.title}>{item.title}</span>
                        <span className="text-xs capitalize text-slate-500">{item.category}</span>
                      </div>
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <FieldError>{formErrors.speciality_ids}</FieldError>
              {selectedSpecialities.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedSpecialities.map((item) => (
                    <Badge key={item.id} variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                      {item.title}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <div className={`overflow-hidden rounded-lg border bg-white ${formErrors.description ? "border-red-300" : "border-slate-200"}`}>
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(value) => handleInputChange("description", value)}
                  modules={quillModules}
                  placeholder="Write description here..."
                />
              </div>
              <FieldError>{formErrors.description}</FieldError>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload
                label={form.is_hod ? "Head Specialist Photo" : "Doctor Photo (Optional)"}
                value={form.image}
                onChange={(file) => {
                  setRemoveImage(false);
                  handleInputChange("image", file);
                }}
                existingFiles={isEditMode && existingImageUrl && !removeImage ? [existingImageUrl] : []}
                onRemoveExisting={() => {
                  setRemoveImage(true);
                  setFormErrors((current) => ({ ...current, image: "" }));
                }}
                accept={IMAGE_TYPES}
                maxSizeBytes={10 * 1024 * 1024}
                required={form.is_hod}
                error={formErrors.image}
                helperText="JPG, PNG, GIF or WEBP — maximum 10 MB"
              />
              <p className="text-xs leading-relaxed text-slate-500">
                {form.is_hod ? "A photo is required for the head specialist's card." : "Photos are optional. This doctor will appear without a photo on speciality pages."}
              </p>
              {isEditMode && removeImage ? (
                <p className="text-xs font-medium text-slate-500">
                  The saved doctor image will be replaced with the default picture.
                </p>
              ) : null}
              <ImageUploadGuidance
                requirementKey="doctorPhoto"
                file={form.image}
                src={!removeImage ? existingImageUrl : ""}
              />
            </div>
          </div>
        </div>

        <SeoFields
          metaTitle={form.meta_title}
          metaDescription={form.meta_description}
          onChange={handleInputChange}
          className="mt-6"
        />

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/doctors")}>Cancel</Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Doctor" : "Create Doctor"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DoctorFormPage;
